import { $patchStyleText } from '@lexical/selection';
import * as Icons from '@mui/icons-material';
import { Box, IconButton, Popover } from '@mui/material';
import { $getSelection, $isRangeSelection, LexicalEditor } from 'lexical';
import React, { useState } from 'react';
import { useStyling } from '../Theme';
import LexicalColorPicker from './LexicalColorPicker';
import { COLOR_PICKER_MODES, lexicalHighlightColors } from './lexicalConstants';

export default function CombinedColorMenu({ editor }: { editor: LexicalEditor }) {
    const { theme } = useStyling();

    const [open, setOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [bgColor, setBgColor] = useState<string>(theme.palette.common.white);
    const [textColor, setTextColor] = useState<string>(theme.palette.text.primary);
    const [mode, setMode] = useState<string>(COLOR_PICKER_MODES.highlight);

    const openForMode = (m: string, target: HTMLElement) => {
        setMode(m);
        setAnchorEl(target);
        setOpen(true);
    };

    const onClose = () => {
        setOpen(false);
        setAnchorEl(null);
    };

    const applyToEditor = (prop: string, newColor: string) => {
        editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
                const node = selection.anchor.getNode();
                // SHOW-6426: Trimming the style string before patching/merging new styles
                // We had some instances where text node styles had trailing spaces causing issues
                node.setStyle(node.getStyle()?.trim());
                $patchStyleText(selection, { [prop]: newColor });
            }
        });
    };

    const activeColor = mode === COLOR_PICKER_MODES.highlight ? bgColor : textColor;

    const handlePickerChange = (newColor: string) => {
        if (mode === COLOR_PICKER_MODES.highlight) {
            setBgColor(newColor);
            // editor expects exact background-color string, so no constants here and on line 52
            applyToEditor('background-color', newColor);
        } else {
            setTextColor(newColor);
            applyToEditor('color', newColor);
        }
    };

    return (
        <>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <IconButton
                    sx={{
                        borderRadius: 0,
                        '&:hover': { backgroundColor: 'action.hover' }
                    }}
                    size="small"
                    onClick={(e) => openForMode(COLOR_PICKER_MODES.highlight, e.currentTarget)}
                >
                    <Icons.FormatColorFill sx={{ width: 16, height: 16 }} />
                    <Icons.KeyboardArrowDown sx={{ fontSize: 18 }} />
                </IconButton>

                <IconButton
                    sx={{
                        borderRadius: 0,
                        '&:hover': { backgroundColor: 'action.hover' }
                    }}
                    size="small"
                    onClick={(e) => openForMode(COLOR_PICKER_MODES.text, e.currentTarget)}
                >
                    <Icons.TextFormat sx={{ width: 18, height: 18 }} />
                    <Icons.KeyboardArrowDown sx={{ fontSize: 18 }} />
                </IconButton>
            </Box>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={onClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
            >
                <LexicalColorPicker
                    value={activeColor}
                    onChange={handlePickerChange}
                    swatches={lexicalHighlightColors}
                    width={220}
                    pickerHeight={120}
                    showHex
                />
            </Popover>
        </>
    );
}
